import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TAGBackground } from '@aspendental/themes';

import { AgendaStepperStepsCollection, ContentTypeRichText, Maybe } from '@/types/generated';
import getHeadingVariant from '@/utils/resolveContentfulHeadingVariant';
import { HeadingTag } from '@/types';
import { textToJumpLink } from '@/utils';

import TAGSvgIcon, { IconName } from '../TAGSvgIcon';
import RichText, { RichTextContent } from '../RichText/RichText';
import TemplateContainer from '../TemplateContainer';

import { getTitleStyles, headingStyles, templateContainerStyles } from './AgendaStepper.styles';
import TAGStepper from './TAGStepper/TAGStepper';

export interface IAgendaStepperProps {
	backgroundColor?: TAGBackground;
	description?: ContentTypeRichText;
	icon?: IconName;
	hideStepperDot: boolean;
	stepsCollection: AgendaStepperStepsCollection;
	title: string;
	titleHtag?: HeadingTag;
	useNumericStepper?: boolean;
	isHighlightedSteps?: boolean;
	areStepsCenteredForMobile?: boolean;
	anchorId?: Maybe<string>;
}

const AgendaStepper = ({
	backgroundColor = 'neutral' as TAGBackground,
	description,
	icon,
	hideStepperDot,
	stepsCollection,
	title,
	titleHtag = 'h1',
	useNumericStepper = false,
	isHighlightedSteps = true,
	areStepsCenteredForMobile = false,
	anchorId,
}: IAgendaStepperProps) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const isDark = backgroundColor?.toLowerCase().includes('dark');
	return (
		<TemplateContainer
			disableGutters={true}
			maxWidth={false}
			containerSx={templateContainerStyles}
			backgroundColor={backgroundColor}
			dataTestId="agenda_stepper_container"
			{...(anchorId && { id: textToJumpLink(anchorId) })}
		>
			{icon && (
				<TAGSvgIcon
					htmlColor={isDark ? 'neutrals.white' : 'tertiary.main'}
					icon={icon}
					size={64}
					dataTestId={'agenda_stepper_icon'}
				/>
			)}
			<Box sx={headingStyles(isSmallScreen, icon)}>
				<Typography
					variant={getHeadingVariant(titleHtag)}
					sx={getTitleStyles(theme)}
					component={titleHtag === 'normal' ? 'p' : titleHtag}
					data-testid={'agenda_stepper_title'}
				>
					{title}
				</Typography>
				{description && (
					<Box data-testid={'agenda_stepper_description'}>
						<RichText
							links={description.content?.links}
							content={description.content?.json as RichTextContent}
						/>
					</Box>
				)}
			</Box>
			<TAGStepper
				hideStepperDot={hideStepperDot}
				stepsCollection={stepsCollection}
				useNumericStepper={useNumericStepper}
				isHighlightedSteps={isHighlightedSteps}
				areStepsCenteredForMobile={areStepsCenteredForMobile}
			/>
		</TemplateContainer>
	);
};

export default AgendaStepper;
